import { useCallback, useEffect, useMemo, useState } from "react";
import Notify from "config/notify";
import { upTireSizeApi } from "../api/carModelApi";
import { ICarTireSizeUp, ICarTireSizeUpItem } from "../types";

interface ResultItem {
  id: keyof ICarTireSizeUp;
  label: string;
  sizes: ICarTireSizeUpItem[];
}

/**
 * Retrieves a key-value pair based on the input ID from the provided data.
 * @param {ResultItem[]} data - The array of ResultItem objects to search through.
 * @param {number} inputId - The ID to search for within the data.
 * @returns {{ key: string, value: string } | null} - The key-value pair if found, or null if not found.
 */
export function getKeyValueFromId(data: ResultItem[], inputId: number, originId: number) {
  const foundGroup = data.find((group) => group.sizes.some((item) => item.mmm_idx === inputId));

  if (foundGroup) {
    const found = foundGroup.sizes.find((item) => item.mmm_idx === inputId);
    if (foundGroup.id === "origin_size") {
      return { key: originId, value: originId };
    }
    return { key: found!.mmm_idx, value: found!.mmm_idx };
  }

  return null;
}

/**
 * Combines tire size data into a structured array of objects.
 * @param {ICarTireSizeUp} tireData - The tire size data containing different upgrade options.
 * @returns {ResultItem[]} - An array of objects where each object contains:
 *   - `label` {string} - The label for the tire size category.
 *   - `sizes` {ICarTireSizeUpItem[]} - The list of tire sizes for the category.
 */
function combineTireSizes(tireData: ICarTireSizeUp): ResultItem[] {
  const result = ["ノーマルサイズ", "1インチアップサイズ", "2インチアップサイズ"];

  const keyMap: { [key: number]: keyof ICarTireSizeUp } = {
    0: "origin_size",
    1: "one_size_up",
    2: "two_size_up",
  };

  return result.map((label, index) => ({
    id: keyMap[index],
    label,
    sizes: tireData[keyMap[index]] || [],
  }));
}

/**
 * Custom hook to fetch and manage the list of tire sizes that can be upgraded
 * for a given car tire size ID.
 * @param {number} car_tiresize_id - The ID of the car tire size to fetch upgrade options for.
 * @returns {object} - An object containing:
 *   - `upTireSizeList` {ICarTireSizeUp[]} - The list of upgradeable tire sizes.
 *   - `loading` {boolean} - The loading state indicating if the data is being fetched.
 *   - `error` {boolean} - Indicates if there was an error during fetching.
 *   - `refetch` {() => Promise<void>} - Function to manually refetch the data.
 */
export default function useUpTireSize(car_tiresize_id: number) {
  const [upTireSizeList, setTireSizeList] = useState<ResultItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  /**
   * Fetches the list of upgradeable tire sizes from the API and updates the state.
   */
  const getUpTireSizeList = useCallback(async () => {
    if (!car_tiresize_id) return;

    setLoading(true);
    setError(false);

    try {
      const data = await upTireSizeApi({ car_tiresize_id });
      if (!data) {
        setTireSizeList([]);
        return;
      }
      if (Object.values(data.list).every((item) => item.length === 0)) {
        setTireSizeList([]);
        Notify.setNotification({
          show: true,
          title: "選択されたタイヤサイズがありません",
        });
        return;
      }

      // Combine tire sizes into a structured format
      const combinedData = combineTireSizes(data.list);
      setTireSizeList(combinedData);
    } catch (err) {
      setError(true);
      setTireSizeList([]);
    } finally {
      setLoading(false);
    }
  }, [car_tiresize_id]);

  /**
   * Effect hook to trigger the API call when the car tire size ID changes.
   */
  useEffect(() => {
    if (car_tiresize_id) {
      getUpTireSizeList();
    } else {
      setTireSizeList([]);
    }
  }, [car_tiresize_id, getUpTireSizeList]);

  // Provide memoized result to prevent unnecessary re-renders
  return useMemo(
    () => ({
      upTireSizeList,
      setTireSizeList,
      loading,
      error,
      refetch: getUpTireSizeList,
    }),
    [upTireSizeList, loading, error, getUpTireSizeList],
  );
}
